/* eslint-disable jsx-a11y/no-autofocus --
 * The single autoFocus in this file moves focus into a control the user just
 * summoned (an inline editor that appears on their own click, or a modal's
 * first field). That is required focus management: leaving focus behind
 * strands keyboard users on an element that has since vanished. The rule
 * guards against autofocus on PAGE LOAD, which is a different thing and is
 * not done anywhere here. File-scoped rather than line-scoped because
 * Prettier reformats JSX props across lines, which silently detaches a
 * line-targeted disable from what it was suppressing.
 */
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  ControlOutlined,
  CaretRightOutlined,
  DeleteOutlined,
  DragOutlined,
  EditOutlined,
  MinusOutlined,
  PlusOutlined,
  RetweetOutlined,
  SaveOutlined,
  StopOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { Button, Input, Popconfirm, Tooltip, Typography } from 'antd';
import type { CSSProperties } from 'react';
import { Fragment, createContext, useContext, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type FileEntryCondition,
  type FormColumn,
  type FormRow,
  type FormSection,
  type WorkflowField,
  type WorkflowStep,
  workflowsApi,
} from '@/api/workflows';
import { BmoModal } from '@/components/BmoModal';
import { handleApiError } from '@/lib/handleApiError';

import { ConditionsEditorModal } from '../conditions/ConditionsEditorModal';
import { hasEligibleSourceFields } from '../conditions/sourceFields';

import type { BuilderDragData, BuilderDropData } from './builderDnd';
import { fieldDisplayLabelKey, metaForFieldType } from './fieldMeta';

const { Text } = Typography;

type Props = {
  sections: FormSection[];
  /** True when a palette card is armed — empty columns highlight + show a place hint. */
  isPalettePending: boolean;
  /** When set, the column is the user's current selection. */
  selectedColumnId: string | null;

  // Section-level
  onAddSection: () => Promise<void>;
  onEditSectionTitle: (sectionId: string, titleEn: string) => Promise<void>;
  onDeleteSection: (sectionId: string) => Promise<void>;
  onMoveSection: (sectionId: string, direction: 'Up' | 'Down') => Promise<void>;

  // Row-level (scoped by sectionId)
  /** `atIndex` places the new row at that slot; omitted appends it at the end. */
  onAddRow: (sectionId: string, atIndex?: number) => Promise<void>;
  onDeleteRow: (sectionId: string, rowId: string) => Promise<void>;
  onMoveRow: (sectionId: string, rowId: string, direction: 'Up' | 'Down') => Promise<void>;
  onAddColumn: (sectionId: string, rowId: string) => Promise<void>;
  onRemoveColumn: (sectionId: string, rowId: string, columnId: string) => Promise<void>;
  onPlaceField: (sectionId: string, rowId: string, columnId: string) => Promise<void>;
  onSelectField: (sectionId: string, rowId: string, columnId: string) => void;

  // ---- Move-field arm-and-place ----
  /** When set, the canvas is in "field armed for move" mode — the source
   *  column is dashed-amber and every empty column highlights as a target. */
  movingFrom: { sectionId: string; rowId: string; columnId: string } | null;
  /** Picks up the field at (section, row, column). Toggle on/off if called
   *  with the same column twice. */
  onArmMove: (sectionId: string, rowId: string, columnId: string) => void;
  /** Drops the armed field into (section, row, column). Caller decides
   *  whether to no-op self-drops vs. clear; this prop just fires. */
  onPlaceMove: (toSectionId: string, toRowId: string, toColumnId: string) => Promise<void>;
  /** Non-null while a drag is in flight. Used to suppress hover tooltips: a
   *  tooltip popping up over a drop target mid-drag is the bug that got drag
   *  removed from this canvas in June (f8eb21b). */
  dragging?: BuilderDragData | null;

  /** Header "Preview" button — opens the read-only Advisor-shaped preview page. Omit to hide the button. */
  onPreview?: () => void;

  // ---- Visibility conditions ----
  /** Owning workflow template id. Needed by the section conditions endpoint URL. */
  templateId: string | undefined;
  stepId: string | undefined;
  /** Every step in the workflow — passed to the visibility modal so the
   *  source-field picker can list fields across all DynamicForm steps. */
  allSteps: WorkflowStep[];
  /** Fired after a successful PUT so the parent can refetch. */
  onConditionsChanged: () => Promise<void>;

  /** Opens the save-as-custom naming modal for this section (parent owns stripping + the POST). */
  onSaveSectionAsCustom: (section: FormSection) => void;

  /** Opens the Section Properties panel for a section (name + repeatable). Omit
   *  to hide the gear affordance — the Custom Editor / non-workflow contexts pass
   *  nothing since section repeatability needs a real workflow template. */
  onSelectSection?: (sectionId: string) => void;
  /** Section currently open in the Section Properties panel — highlights its header. */
  selectedSectionId?: string | null;

  /** Hides "+ Add Section" — the Custom Editor's Section mode holds exactly one section. */
  hideAddSection?: boolean;
  /** Renders a "Step details" button beside Preview — the Custom Editor's Step mode uses this to open its name/description modal. Omitted for real workflow steps, which rename via the step-list page instead. */
  onEditStepDetails?: () => void;
  /** In-memory save for section conditions — used by the custom step editor which has no real templateId. When provided, replaces the workflowsApi call in the conditions modal. */
  onSaveSectionConditions?: (
    sectionId: string,
    conditions: FileEntryCondition[],
    combinator: 'all' | 'any' | null,
  ) => Promise<void>;
  /** Same in-memory lane as {@link onSaveSectionConditions}, for the "Disable if" rule. */
  onSaveSectionDisableConditions?: (
    sectionId: string,
    conditions: FileEntryCondition[],
    combinator: 'all' | 'any' | null,
  ) => Promise<void>;
};

/**
 * fieldId → label of the RadioGroup / CheckboxGroup whose option borrows it via
 * `revealFieldId`.
 *
 * A borrowed field does NOT render where it is placed — the runtime lifts it
 * into a slot under its owning option (see `indexStepFields` in FormRenderer).
 * Without a marker the canvas shows it sitting in a section it will never
 * appear in, which reads as a bug. Fields in here render greyed, with an
 * "inline-reveal: <owner>" tag, and can't be dragged (their position is
 * meaningless). They stay CLICKABLE so their own properties are still
 * editable — the borrow is undone from the owning option's Option behaviour
 * modal, not from here.
 *
 * A context rather than a prop so it doesn't have to thread through
 * SectionCard → RowCard → ColumnSlot → FieldPreview, none of which otherwise
 * care.
 */
const BorrowedFieldsContext = createContext<ReadonlyMap<string, string>>(new Map());

/** Walk the canvas's sections for every option that borrows a field.
 *  Exported for tests — the canvas itself has no render coverage. */
export function indexBorrowedFields(
  sections: FormSection[],
  fallbackLabel: (field: WorkflowField) => string,
): Map<string, string> {
  const borrowed = new Map<string, string>();
  for (const section of sections) {
    for (const row of section.rows ?? []) {
      for (const col of row.columns ?? []) {
        const owner = col.field;
        if (!owner) continue;
        if (owner.type !== 'RadioGroup' && owner.type !== 'CheckboxGroup') continue;
        for (const option of owner.options ?? []) {
          if (option.revealFieldId) {
            borrowed.set(option.revealFieldId, owner.labelEn || fallbackLabel(owner));
          }
        }
      }
    }
  }
  return borrowed;
}

/**
 * sectionId → label of the RadioGroup / CheckboxGroup whose option borrows the
 * whole section via `revealSectionId`.
 *
 * `BorrowedFieldsContext`'s plural, one level up the tree and for exactly the
 * same reason: the runtime lifts a borrowed SECTION out of page flow
 * (`indexStepFields` / `flowSections` in FormRenderer) and renders it only
 * inside its owning option's card, so a canvas that draws it as an ordinary
 * standalone section is showing the author something that will never happen.
 * Borrowed sections render muted with an "inline-reveal: <owner>" tag and lose
 * their move controls (position is meaningless). They stay selectable,
 * renamable and deletable — the borrow is undone from the owning option's
 * Option behaviour modal, not from here.
 */
const BorrowedSectionsContext = createContext<ReadonlyMap<string, string>>(new Map());

/** Walk the canvas's sections for every option that borrows a whole section.
 *  Exported for tests — the canvas itself has no render coverage. */
export function indexBorrowedSections(
  sections: FormSection[],
  fallbackLabel: (field: WorkflowField) => string,
): Map<string, string> {
  const borrowed = new Map<string, string>();
  for (const section of sections) {
    for (const row of section.rows ?? []) {
      for (const col of row.columns ?? []) {
        const owner = col.field;
        if (!owner) continue;
        if (owner.type !== 'RadioGroup' && owner.type !== 'CheckboxGroup') continue;
        for (const option of owner.options ?? []) {
          if (option.revealSectionId) {
            borrowed.set(option.revealSectionId, owner.labelEn || fallbackLabel(owner));
          }
        }
      }
    }
  }
  return borrowed;
}

const CANVAS: CSSProperties = {
  flex: 1,
  minWidth: 0,
  overflowY: 'auto',
  background: '#f5f7fa',
  padding: 20,
};

const HEADER_BAR: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: 8,
  marginBottom: 16,
};

const SECTION_CARD: CSSProperties = {
  background: '#fff',
  borderRadius: 14,
  border: '1px solid #e5e7eb',
  marginBottom: 18,
  overflow: 'hidden',
};

// Two groups, not a flat row of eleven items: the title group and the action
// cluster each need to wrap as a unit, and only a group can be given the
// full-width basis that drops the buttons below the name on a phone.
const SECTION_HEADER: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: 10,
  background: 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)',
  borderBottom: '1px solid #e5e7eb',
  padding: '12px 16px',
};

const SECTION_HEADER_TITLE: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  // `1 1 auto`, so a long section name takes the width it needs up to the row
  // and then wraps — rather than being shrunk to a one-character column by the
  // buttons, which hold their intrinsic width.
  flex: '1 1 auto',
  minWidth: 0,
};

const SECTION_HEADER_ACTIONS: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 10,
  flexShrink: 0,
};

const SECTION_BODY: CSSProperties = {
  padding: 16,
};

const ROW_CARD: CSSProperties = {
  background: '#fafafa',
  borderRadius: 10,
  border: '1px solid #e5e7eb',
  // No bottom margin — the dashed insert line between rows supplies the gap.
  overflow: 'hidden',
};

const ROW_HEADER: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: 8,
  background: '#f3f4f6',
  padding: '8px 12px',
  borderBottom: '1px solid #e5e7eb',
};

// `grid-template-columns` is NOT here — it is `.fb-row-body` in global.css,
// built from the `--fb-row-cols` custom property this row publishes. The count
// is capped to two on a tablet and one on a phone, where four 1fr tracks give
// each field ~80px, and a cap can only be applied by a rule that outranks the
// element — which an inline longhand never is.
const ROW_BODY: CSSProperties = {
  display: 'grid',
  gap: 12,
  padding: 14,
};

const ROW_BTN: CSSProperties = {
  height: 24,
  padding: '0 8px',
  fontSize: 12,
  lineHeight: 1,
};

const ROW_ICON_BTN: CSSProperties = {
  ...ROW_BTN,
  width: 24,
  padding: 0,
};

const COLUMN_SLOT: CSSProperties = {
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  padding: '10px 12px',
  background: '#fff',
  minHeight: 52,
  display: 'flex',
  alignItems: 'center',
  transition: 'border-color 140ms ease, background 140ms ease',
};

// Row insert affordance — a dashed rule sitting between rows (and after the
// last one) that reveals an "Add Row" pill on hover/focus. Replaced the single
// "+ Add Row" footer pill so a row can be born where it belongs instead of
// always at the end and then walked up with the move buttons.
const INSERT_LINE: CSSProperties = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  height: 26,
  padding: 0,
  border: 'none',
  background: 'none',
  cursor: 'pointer',
};

// One unbroken rule — the pill floats over its middle rather than splitting it,
// so the line reads as continuous at rest instead of showing a hole where the
// hidden pill would sit.
const INSERT_RULE: CSSProperties = {
  flex: 1,
  borderTop: '1px dashed #e5e7eb',
  transition: 'border-color 140ms ease',
};

const INSERT_PILL: CSSProperties = {
  position: 'absolute',
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  borderRadius: 999,
  border: '1px solid #bfdbfe',
  background: '#eff6ff',
  color: '#1d4ed8',
  fontSize: 12,
  fontWeight: 500,
  lineHeight: 1,
  padding: '5px 12px',
  transition: 'opacity 140ms ease',
};

/**
 * Center pane of the builder. Top-level list of sections; each section has its
 * own row list inside. "+ Add Section" is the primary canvas action. Drag-to-
 * reorder works at both levels (sections vertically; rows within a section).
 * The empty state nudges the originator to add the first section explicitly so
 * the section ID is real before any rows go in.
 */
export function FormCanvas(props: Props) {
  const {
    sections,
    isPalettePending,
    selectedColumnId,
    movingFrom,
    onAddSection,
    onEditSectionTitle,
    onDeleteSection,
    onMoveSection,
    onAddRow,
    onDeleteRow,
    onMoveRow,
    onAddColumn,
    onRemoveColumn,
    onPlaceField,
    onSelectField,
    onArmMove,
    onPlaceMove,
    dragging,
    onPreview,
    templateId,
    stepId,
    allSteps,
    onConditionsChanged,
    onSaveSectionAsCustom,
    hideAddSection = false,
    onEditStepDetails,
    onSaveSectionConditions,
    onSaveSectionDisableConditions,
    onSelectSection,
    selectedSectionId,
  } = props;
  const { t } = useTranslation();
  const safeSections = sections ?? [];

  // Which fields are borrowed as an option's inline reveal, and by whom.
  // Computed once per sections change and shared down via context.
  const borrowedFields = useMemo(
    () => indexBorrowedFields(sections ?? [], (f) => t(fieldDisplayLabelKey(f))),
    [sections, t],
  );

  // Same, one level up: which whole SECTIONS are borrowed as an option's inline
  // reveal, and by whom.
  const borrowedSections = useMemo(
    () => indexBorrowedSections(sections ?? [], (f) => t(fieldDisplayLabelKey(f))),
    [sections, t],
  );

  // Section whose visibility modal is open. Lives at the canvas root so one
  // modal serves every section card without re-mounting.
  const [conditionTarget, setConditionTarget] = useState<FormSection | null>(null);
  const [savingConditions, setSavingConditions] = useState(false);
  // The "Disable if" twin — its own target + saving state so the two rules never
  // share a draft.
  const [disableTarget, setDisableTarget] = useState<FormSection | null>(null);
  const [savingDisableConditions, setSavingDisableConditions] = useState(false);

  // Esc handling for the armed move used to live here. It now sits in
  // DynamicFormEditorShell alongside the other three cancellable states
  // (palette arm, field selection, section selection) so one keypress clears
  // exactly one of them in a defined order. Two independent listeners would
  // each fire on the same Esc and skip a level.

  return (
    <BorrowedFieldsContext.Provider value={borrowedFields}>
      <BorrowedSectionsContext.Provider value={borrowedSections}>
        <section className="fb-canvas" style={CANVAS}>
          <div style={HEADER_BAR}>
            {!hideAddSection && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => void onAddSection()}>
                {t('workflow.builder.addSection')}
              </Button>
            )}
            <div
              style={{ display: 'flex', gap: 8, marginLeft: hideAddSection ? 'auto' : undefined }}
            >
              {onPreview && <Button onClick={onPreview}>{t('workflow.builder.preview')}</Button>}
              {onEditStepDetails ? (
                <Button onClick={onEditStepDetails}>
                  {t('workflow.customElements.stepDetails')}
                </Button>
              ) : (
                <Button type="primary" disabled>
                  {t('workflow.builder.save')}
                </Button>
              )}
            </div>
          </div>

          {safeSections.length === 0 ? (
            <EmptyCanvas onAddSection={onAddSection} />
          ) : (
            safeSections.map((section, index) => {
              // Hide the "Add showing condition" button entirely when there's
              // nothing the user could pick. Still shown when conditions are
              // already set so the user can clear them.
              const showConditionsButton =
                (section.conditions?.length ?? 0) > 0 ||
                (stepId !== undefined &&
                  hasEligibleSourceFields(allSteps, {
                    kind: 'section',
                    ownStepId: stepId,
                    ownSectionId: section.id,
                  }));
              return (
                <SectionCard
                  key={section.id}
                  section={section}
                  isFirst={index === 0}
                  isLast={index === safeSections.length - 1}
                  isPalettePending={isPalettePending}
                  selectedColumnId={selectedColumnId}
                  movingFrom={movingFrom}
                  dragging={dragging}
                  sectionId={section.id}
                  onEditTitle={(t2) => void onEditSectionTitle(section.id, t2)}
                  onDelete={() => void onDeleteSection(section.id)}
                  onMoveUp={() => void onMoveSection(section.id, 'Up')}
                  onMoveDown={() => void onMoveSection(section.id, 'Down')}
                  onAddRow={(atIndex) => void onAddRow(section.id, atIndex)}
                  onDeleteRow={(rowId) => void onDeleteRow(section.id, rowId)}
                  onMoveRow={(rowId, dir) => void onMoveRow(section.id, rowId, dir)}
                  onAddColumn={(rowId) => void onAddColumn(section.id, rowId)}
                  onRemoveColumn={(rowId, colId) => void onRemoveColumn(section.id, rowId, colId)}
                  onPlaceField={(rowId, colId) => void onPlaceField(section.id, rowId, colId)}
                  onSelectField={(rowId, colId) => onSelectField(section.id, rowId, colId)}
                  onArmMove={(rowId, colId) => onArmMove(section.id, rowId, colId)}
                  onPlaceMove={(rowId, colId) => onPlaceMove(section.id, rowId, colId)}
                  onEditConditions={showConditionsButton ? () => setConditionTarget(section) : null}
                  onEditDisableConditions={
                    showConditionsButton ? () => setDisableTarget(section) : null
                  }
                  onSaveAsCustom={() => onSaveSectionAsCustom(section)}
                  onSelectSection={onSelectSection ? () => onSelectSection(section.id) : undefined}
                  isSelected={selectedSectionId === section.id}
                />
              );
            })
          )}
          {((templateId != null && stepId != null) || onSaveSectionConditions != null) && (
            <ConditionsEditorModal
              open={conditionTarget !== null}
              title={t('workflow.builder.section.visibilityModalTitle')}
              intro={t('workflow.builder.section.visibilityIntro')}
              initialConditions={conditionTarget?.conditions ?? []}
              initialCombinator={conditionTarget?.conditionCombinator ?? null}
              allSteps={allSteps}
              scope={
                conditionTarget && stepId
                  ? { kind: 'section', ownStepId: stepId, ownSectionId: conditionTarget.id }
                  : { kind: 'pdf' }
              }
              saving={savingConditions}
              onCancel={() => setConditionTarget(null)}
              onSave={async (conditions, combinator) => {
                if (!conditionTarget) return;
                setSavingConditions(true);
                try {
                  if (onSaveSectionConditions) {
                    await onSaveSectionConditions(conditionTarget.id, conditions, combinator);
                  } else {
                    await workflowsApi.setSectionConditions(
                      templateId!,
                      stepId!,
                      conditionTarget.id,
                      {
                        conditions,
                        combinator,
                      },
                    );
                    await onConditionsChanged();
                  }
                  setConditionTarget(null);
                } catch (err) {
                  handleApiError(err, t, t('workflow.builder.section.visibilitySaveFailed'));
                } finally {
                  setSavingConditions(false);
                }
              }}
            />
          )}
          {/* Section-level "Disable if" — same shared modal, same scope, different
              effect and endpoint. See FormSection.disableConditions. */}
          {((templateId != null && stepId != null) || onSaveSectionDisableConditions != null) && (
            <ConditionsEditorModal
              open={disableTarget !== null}
              title={t('workflow.builder.section.disableModalTitle')}
              intro={t('workflow.builder.section.disableIntro')}
              initialConditions={disableTarget?.disableConditions ?? []}
              initialCombinator={disableTarget?.disableConditionCombinator ?? null}
              allSteps={allSteps}
              scope={
                disableTarget && stepId
                  ? { kind: 'section', ownStepId: stepId, ownSectionId: disableTarget.id }
                  : { kind: 'pdf' }
              }
              saving={savingDisableConditions}
              onCancel={() => setDisableTarget(null)}
              onSave={async (conditions, combinator) => {
                if (!disableTarget) return;
                setSavingDisableConditions(true);
                try {
                  if (onSaveSectionDisableConditions) {
                    await onSaveSectionDisableConditions(disableTarget.id, conditions, combinator);
                  } else {
                    await workflowsApi.setSectionDisableConditions(
                      templateId!,
                      stepId!,
                      disableTarget.id,
                      { conditions, combinator },
                    );
                    await onConditionsChanged();
                  }
                  setDisableTarget(null);
                } catch (err) {
                  handleApiError(err, t, t('workflow.builder.section.disableSaveFailed'));
                } finally {
                  setSavingDisableConditions(false);
                }
              }}
            />
          )}
        </section>
      </BorrowedSectionsContext.Provider>
    </BorrowedFieldsContext.Provider>
  );
}

function EmptyCanvas({ onAddSection }: { onAddSection: () => Promise<void> }) {
  const { t } = useTranslation();
  return (
    <div
      style={{
        background: '#fff',
        border: '1px dashed #cbd5e1',
        borderRadius: 12,
        padding: '48px 24px',
        textAlign: 'center',
      }}
    >
      <Text strong style={{ display: 'block', fontSize: 15, marginBottom: 6 }}>
        {t('workflow.builder.canvas.emptyTitle')}
      </Text>
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        {t('workflow.builder.canvas.emptySubtitle')}
      </Text>
      <Button type="primary" icon={<PlusOutlined />} onClick={() => void onAddSection()}>
        {t('workflow.builder.addSection')}
      </Button>
    </div>
  );
}

type SectionProps = {
  section: FormSection;
  /** Owning section id — needed for move-field source comparisons inside
   *  ColumnSlot since FormSection itself doesn't carry the id at that depth. */
  sectionId: string;
  isFirst: boolean;
  isLast: boolean;
  isPalettePending: boolean;
  selectedColumnId: string | null;
  movingFrom: { sectionId: string; rowId: string; columnId: string } | null;
  dragging?: BuilderDragData | null;
  onEditTitle: (title: string) => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onAddRow: (atIndex?: number) => void;
  onDeleteRow: (rowId: string) => void;
  onMoveRow: (rowId: string, direction: 'Up' | 'Down') => void;
  onAddColumn: (rowId: string) => void;
  onRemoveColumn: (rowId: string, columnId: string) => void;
  onPlaceField: (rowId: string, columnId: string) => void;
  onSelectField: (rowId: string, columnId: string) => void;
  onArmMove: (rowId: string, columnId: string) => void;
  onPlaceMove: (rowId: string, columnId: string) => Promise<void>;
  /** Fires the section visibility-conditions modal on the parent. Null when
   *  there are no eligible source fields for this section's scope AND no
   *  conditions are already set — we hide the button entirely. */
  onEditConditions: (() => void) | null;
  /** Same contract as {@link onEditConditions}, for the "Disable if" rule. */
  onEditDisableConditions: (() => void) | null;
  onSaveAsCustom: () => void;
  /** Opens the Section Properties panel. Undefined hides the gear button. */
  onSelectSection?: () => void;
  /** True when this section is the one open in the Section Properties panel. */
  isSelected?: boolean;
};

/**
 * Section title fallback: when the originator hasn't typed a title we use the
 * first StaticText / Paragraph block's EN label as the displayed name. Keeps
 * the canvas readable for sections that lead with a heading-style text block.
 */
function firstTextBlockLabelEn(section: FormSection): string | null {
  for (const row of section.rows) {
    for (const col of row.columns) {
      const f = col.field;
      if (!f) continue;
      if (f.type === 'StaticText' || f.type === 'Paragraph') {
        if (f.labelEn?.trim()) return f.labelEn.trim();
      }
    }
  }
  return null;
}

function SectionCard({
  section,
  sectionId,
  isFirst,
  isLast,
  isPalettePending,
  selectedColumnId,
  movingFrom,
  dragging,
  onEditTitle,
  onDelete,
  onMoveUp,
  onMoveDown,
  onAddRow,
  onDeleteRow,
  onMoveRow,
  onAddColumn,
  onRemoveColumn,
  onPlaceField,
  onSelectField,
  onArmMove,
  onPlaceMove,
  onEditConditions,
  onEditDisableConditions,
  onSaveAsCustom,
  onSelectSection,
  isSelected = false,
}: SectionProps) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(section.titleEn);
  // Section collapsed/expanded state. Local — refresh resets to expanded. If
  // we want persistence later, key by `${step.id}:${section.id}` in
  // localStorage; for now the lighter touch keeps things simple.
  const [collapsed, setCollapsed] = useState(false);
  // Borrowed by an option's inline reveal — this section renders under that
  // option, not here. See BorrowedSectionsContext.
  const borrowedSections = useContext(BorrowedSectionsContext);
  const borrowedBy = borrowedSections.get(sectionId) ?? null;

  // Idempotent commit so pressing Enter (which calls inputRef.blur()) and the
  // subsequent onBlur from React's unmount don't fire onEditTitle twice — that
  // produced a duplicate PATCH /sections/{id} per rename.
  const committedRef = useRef(false);
  const commitTitle = () => {
    if (committedRef.current) return;
    committedRef.current = true;
    const trimmed = draft.trim();
    if (trimmed && trimmed !== section.titleEn) onEditTitle(trimmed);
    setEditing(false);
  };

  const startEditing = () => {
    setDraft(section.titleEn);
    committedRef.current = false;
    setEditing(true);
  };

  return (
    <div
      style={{
        ...SECTION_CARD,
        // Borrowed: muted so it reads as "not live in this position", matching
        // what ColumnSlot does for a borrowed FIELD.
        ...(borrowedBy !== null
          ? { background: '#f8fafc', borderStyle: 'dashed', borderColor: '#cbd5e1' }
          : {}),
        // Selection wins over the muted treatment — a borrowed section is still
        // selectable and the author needs to see which one they are editing.
        ...(isSelected ? { borderColor: '#0179C1', boxShadow: '0 0 0 1px #0179C1' } : {}),
      }}
    >
      <div style={SECTION_HEADER}>
        <div style={SECTION_HEADER_TITLE}>
          <Tooltip
            title={t(
              collapsed ? 'workflow.builder.section.expand' : 'workflow.builder.section.collapse',
            )}
          >
            <button
              type="button"
              onClick={() => setCollapsed((c) => !c)}
              aria-label={t(
                collapsed ? 'workflow.builder.section.expand' : 'workflow.builder.section.collapse',
              )}
              aria-expanded={!collapsed}
              style={{
                border: 'none',
                background: 'transparent',
                padding: 0,
                cursor: 'pointer',
                color: '#6b7280',
                fontSize: 12,
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              <CaretRightOutlined
                style={{
                  transition: 'transform 140ms ease',
                  transform: collapsed ? 'rotate(0deg)' : 'rotate(90deg)',
                }}
              />
            </button>
          </Tooltip>
          {editing ? (
            // Wrapper takes the same flex:1 slot the static title button does,
            // so the right-side controls (move up/down, delete) stay anchored
            // regardless of edit-vs-display mode. The input itself caps at
            // 320 px inside that slot so it doesn't grow uncomfortably wide.
            <div style={{ flex: 1, minWidth: 0 }}>
              <Input
                autoFocus
                size="small"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onPressEnter={commitTitle}
                onBlur={commitTitle}
                style={{ maxWidth: 320 }}
              />
            </div>
          ) : (
            <>
              <button
                type="button"
                onClick={startEditing}
                title={t('workflow.builder.section.editTitle')}
                style={{
                  border: 'none',
                  background: 'transparent',
                  padding: 0,
                  cursor: 'text',
                  textAlign: 'left',
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <Text strong style={{ fontSize: 15, color: '#111827' }}>
                  {section.titleEn ||
                    firstTextBlockLabelEn(section) ||
                    t('workflow.builder.section.untitled')}
                </Text>
              </button>
              <Tooltip title={t('workflow.builder.section.editTitle')}>
                <Button
                  size="small"
                  style={ROW_ICON_BTN}
                  icon={<EditOutlined style={{ fontSize: 11 }} />}
                  onClick={startEditing}
                  aria-label={t('workflow.builder.section.editTitle')}
                />
              </Tooltip>
            </>
          )}
          {/* Borrowed-section tag. Same copy and styling as the borrowed-FIELD
            tag in ColumnSlot so the two read as one concept at both levels. */}
          {borrowedBy !== null && (
            <Tooltip title={t('workflow.builder.section.inlineRevealHint', { field: borrowedBy })}>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  fontSize: 10,
                  lineHeight: 1,
                  fontWeight: 600,
                  color: '#475569',
                  background: '#e2e8f0',
                  padding: '4px 8px',
                  borderRadius: 999,
                  flexShrink: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {t('workflow.builder.section.inlineRevealTag', { field: borrowedBy })}
              </span>
            </Tooltip>
          )}
        </div>
        <div className="fb-section-header__actions" style={SECTION_HEADER_ACTIONS}>
          {/* Repeating-section chiclet — read-only badge beside the visibility
            pill so the canvas surfaces that this section repeats without the
            originator having to open the Section Properties panel. Neutral grey
            so it reads as a status, not a clickable action like the blue
            visibility pill next to it. */}
          {section.repeatable && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 12,
                fontWeight: 500,
                color: '#475569',
                background: '#F1F5F9',
                border: '1px solid #e2e8f0',
                padding: '4px 10px',
                borderRadius: 999,
                whiteSpace: 'nowrap',
              }}
            >
              <RetweetOutlined style={{ fontSize: 12 }} />
              {t('workflow.builder.section.repeatingChiclet')}
            </span>
          )}
          {/* Visibility pill — BMO-blue outlined button so it reads as
            clickable (the earlier grey-on-grey looked like a status badge).
            Filled-blue when conditions are set; clicking opens the shared
            conditions modal at the canvas root. Hidden entirely when the
            parent passed onEditConditions=null (no eligible source fields). */}
          {onEditConditions && (
            <Tooltip
              title={t(
                section.conditions?.length
                  ? 'workflow.builder.section.visibilityEdit'
                  : 'workflow.builder.section.visibilityAdd',
                { count: section.conditions?.length ?? 0 },
              )}
            >
              <button
                type="button"
                onClick={onEditConditions}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 12,
                  fontWeight: 500,
                  color: section.conditions?.length ? '#fff' : '#0179C1',
                  background: section.conditions?.length ? '#0179C1' : '#EFF6FF',
                  border: '1px solid #0179C1',
                  padding: '4px 10px',
                  borderRadius: 999,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                <ControlOutlined style={{ fontSize: 12 }} />
                {section.conditions?.length
                  ? t('workflow.builder.section.visibilityCount', {
                      count: section.conditions.length,
                    })
                  : t('workflow.builder.section.visibility')}
              </button>
            </Tooltip>
          )}
          {/* "Disable if" pill — the twin of the one above, in amber so the two rules
            stay distinguishable. A section can carry both; they mean opposite things
            for whether its answers reach BMO. */}
          {onEditDisableConditions && (
            <Tooltip
              title={t(
                section.disableConditions?.length
                  ? 'workflow.builder.section.disableEdit'
                  : 'workflow.builder.section.disableAdd',
                { count: section.disableConditions?.length ?? 0 },
              )}
            >
              <button
                type="button"
                onClick={onEditDisableConditions}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 12,
                  fontWeight: 500,
                  color: section.disableConditions?.length ? '#fff' : '#B45309',
                  background: section.disableConditions?.length ? '#B45309' : '#FFFBEB',
                  border: '1px solid #B45309',
                  padding: '4px 10px',
                  borderRadius: 999,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                <StopOutlined style={{ fontSize: 12 }} />
                {section.disableConditions?.length
                  ? t('workflow.builder.section.disableCount', {
                      count: section.disableConditions.length,
                    })
                  : t('workflow.builder.section.disable')}
              </button>
            </Tooltip>
          )}
          {onSelectSection && (
            <Tooltip title={t('workflow.builder.section.editProperties')}>
              <Button
                size="small"
                type={isSelected ? 'primary' : 'default'}
                style={ROW_ICON_BTN}
                icon={<SettingOutlined style={{ fontSize: 11 }} />}
                onClick={onSelectSection}
                aria-label={t('workflow.builder.section.editProperties')}
              />
            </Tooltip>
          )}
          <Tooltip title={t('workflow.builder.section.saveAsCustom')}>
            <Button
              size="small"
              style={ROW_ICON_BTN}
              icon={<SaveOutlined style={{ fontSize: 11 }} />}
              onClick={onSaveAsCustom}
              aria-label={t('workflow.builder.section.saveAsCustom')}
            />
          </Tooltip>
          {/* No move affordance on a borrowed section — its position in the step
            decides nothing, because the runtime renders it under its owning
            option wherever that option happens to sit. Buttons that reorder
            something invisible are worse than no buttons. */}
          {borrowedBy === null && (
            <>
              <Tooltip title={t('workflow.builder.section.moveUp')}>
                <Button
                  size="small"
                  style={ROW_ICON_BTN}
                  icon={<ArrowUpOutlined style={{ fontSize: 11 }} />}
                  disabled={isFirst}
                  onClick={onMoveUp}
                />
              </Tooltip>
              <Tooltip title={t('workflow.builder.section.moveDown')}>
                <Button
                  size="small"
                  style={ROW_ICON_BTN}
                  icon={<ArrowDownOutlined style={{ fontSize: 11 }} />}
                  disabled={isLast}
                  onClick={onMoveDown}
                />
              </Tooltip>
            </>
          )}
          <Popconfirm
            title={t('workflow.builder.section.deleteConfirmTitle')}
            description={t('workflow.builder.section.deleteConfirmDesc')}
            okText={t('workflow.builder.section.deleteConfirmOk')}
            cancelText={t('common.cancel')}
            okButtonProps={{ danger: true }}
            onConfirm={onDelete}
            placement="topRight"
          >
            <Button
              size="small"
              danger
              icon={<DeleteOutlined style={{ fontSize: 12 }} />}
              style={ROW_BTN}
            >
              {t('workflow.builder.section.delete')}
            </Button>
          </Popconfirm>
        </div>
      </div>

      {!collapsed && (
        <div style={SECTION_BODY}>
          {section.rows.length === 0 && (
            <div
              style={{
                padding: '16px',
                textAlign: 'center',
                border: '1px dashed #cbd5e1',
                borderRadius: 8,
                background: '#fafafa',
                color: '#64748b',
                fontSize: 13,
              }}
            >
              {t('workflow.builder.section.empty')}
            </div>
          )}

          {/* Each row is preceded by its own insert line, and one trailing line
              closes the list — so a new row can be born anywhere instead of
              always at the end. Compounds used to live in a footer strip here
              but moved to the left palette on 2026-06-17 (clicking a compound
              card creates a new section pre-populated with the blueprint). */}
          {section.rows.map((row, idx) => (
            <Fragment key={row.id}>
              <RowInsertLine onInsert={() => onAddRow(idx)} />
              <RowCard
                row={row}
                sectionId={sectionId}
                isFirst={idx === 0}
                isLast={idx === section.rows.length - 1}
                isPalettePending={isPalettePending}
                selectedColumnId={selectedColumnId}
                movingFrom={movingFrom}
                dragging={dragging}
                onDelete={() => onDeleteRow(row.id)}
                onMoveUp={() => onMoveRow(row.id, 'Up')}
                onMoveDown={() => onMoveRow(row.id, 'Down')}
                onAddColumn={() => onAddColumn(row.id)}
                onRemoveColumn={(colId) => onRemoveColumn(row.id, colId)}
                onPlaceField={(colId) => onPlaceField(row.id, colId)}
                onSelectField={(colId) => onSelectField(row.id, colId)}
                onArmMove={(colId) => onArmMove(row.id, colId)}
                onPlaceMove={(colId) => onPlaceMove(row.id, colId)}
              />
            </Fragment>
          ))}
          {/* No index — appends, which is also the only line an empty section has. */}
          <RowInsertLine onInsert={() => onAddRow()} />
        </div>
      )}
    </div>
  );
}

/**
 * The dashed gap between two rows. The rule is always drawn so the insertion
 * point is discoverable at rest; the "Add Row" pill fades in on hover or
 * keyboard focus. A real <button> rather than a styled div so the affordance
 * is reachable by tab and announced by its label.
 */
function RowInsertLine({ onInsert }: { onInsert: () => void }) {
  const { t } = useTranslation();
  const [active, setActive] = useState(false);
  const rule = active ? { ...INSERT_RULE, borderTopColor: '#60a5fa' } : INSERT_RULE;

  return (
    <button
      type="button"
      style={INSERT_LINE}
      aria-label={t('workflow.builder.addRow')}
      onClick={onInsert}
      onMouseEnter={() => setActive(true)}
      onMouseLeave={() => setActive(false)}
      onFocus={() => setActive(true)}
      onBlur={() => setActive(false)}
    >
      <span style={rule} />
      <span aria-hidden style={{ ...INSERT_PILL, opacity: active ? 1 : 0 }}>
        <PlusOutlined style={{ fontSize: 11 }} />
        {t('workflow.builder.addRow')}
      </span>
    </button>
  );
}

type RowCardProps = {
  row: FormRow;
  isFirst: boolean;
  isLast: boolean;
  sectionId: string;
  isPalettePending: boolean;
  selectedColumnId: string | null;
  movingFrom: { sectionId: string; rowId: string; columnId: string } | null;
  dragging?: BuilderDragData | null;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onAddColumn: () => void;
  onRemoveColumn: (columnId: string) => void;
  onPlaceField: (columnId: string) => void;
  onSelectField: (columnId: string) => void;
  onArmMove: (columnId: string) => void;
  onPlaceMove: (columnId: string) => Promise<void>;
};

function RowCard({
  row,
  sectionId,
  isFirst,
  isLast,
  isPalettePending,
  selectedColumnId,
  movingFrom,
  dragging,
  onDelete,
  onMoveUp,
  onMoveDown,
  onAddColumn,
  onRemoveColumn,
  onPlaceField,
  onSelectField,
  onArmMove,
  onPlaceMove,
}: RowCardProps) {
  const { t } = useTranslation();
  const columnCount = row.columns.length;
  // Guard row deletion behind a confirm modal — removing a row takes its
  // columns and any placed fields with it, and the builder has no undo. The
  // modal names every field going with it so the originator can tell two
  // similar-looking rows apart before confirming.
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const placedFields = row.columns
    .map((c) => c.field)
    .filter((f): f is WorkflowField => f !== null);

  return (
    <div style={ROW_CARD}>
      <div style={ROW_HEADER}>
        <Text strong style={{ fontSize: 11, letterSpacing: 0.6, color: '#6b7280' }}>
          {t('workflow.builder.row.columnCount', { count: columnCount })}
        </Text>
        <div
          className="fb-row-header__actions"
          style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}
        >
          <Tooltip title={t('workflow.builder.row.moveUp')}>
            <Button
              size="small"
              style={ROW_ICON_BTN}
              icon={<ArrowUpOutlined style={{ fontSize: 11 }} />}
              disabled={isFirst}
              onClick={onMoveUp}
            />
          </Tooltip>
          <Tooltip title={t('workflow.builder.row.moveDown')}>
            <Button
              size="small"
              style={ROW_ICON_BTN}
              icon={<ArrowDownOutlined style={{ fontSize: 11 }} />}
              disabled={isLast}
              onClick={onMoveDown}
            />
          </Tooltip>
          <Button
            size="small"
            style={ROW_BTN}
            icon={<MinusOutlined style={{ fontSize: 11 }} />}
            disabled={columnCount <= 1}
            onClick={() => {
              // Prefer an empty column (rightmost first) so clearing a field
              // then shrinking the row doesn't force-target an occupied
              // column and trip the "remove the field first" domain error.
              const target =
                [...row.columns].reverse().find((c) => c.field === null) ??
                row.columns[row.columns.length - 1];
              if (target) onRemoveColumn(target.id);
            }}
          >
            {t('workflow.builder.row.removeColumn')}
          </Button>
          <Button
            size="small"
            style={ROW_BTN}
            icon={<PlusOutlined style={{ fontSize: 11 }} />}
            disabled={columnCount >= 4}
            onClick={onAddColumn}
          >
            {t('workflow.builder.row.addColumn')}
          </Button>
          <Button
            size="small"
            danger
            style={ROW_BTN}
            icon={<DeleteOutlined style={{ fontSize: 11 }} />}
            onClick={() => setDeleteConfirmOpen(true)}
          >
            {t('workflow.builder.row.delete')}
          </Button>
        </div>
      </div>

      <BmoModal
        open={deleteConfirmOpen}
        onCancel={() => setDeleteConfirmOpen(false)}
        title={t('workflow.builder.row.deleteConfirmTitle')}
        width={440}
        footer={[
          <Button key="cancel" onClick={() => setDeleteConfirmOpen(false)}>
            {t('common.cancel')}
          </Button>,
          <Button
            key="delete"
            danger
            type="primary"
            icon={<DeleteOutlined />}
            onClick={() => {
              setDeleteConfirmOpen(false);
              onDelete();
            }}
          >
            {t('workflow.builder.row.deleteConfirmOk')}
          </Button>,
        ]}
      >
        {t('workflow.builder.row.deleteConfirmBody')}

        {placedFields.length === 0 ? (
          <div style={{ marginTop: 12, color: '#64748b', fontSize: 13 }}>
            {t('workflow.builder.row.deleteConfirmNoFields')}
          </div>
        ) : (
          <>
            <div style={{ marginTop: 12, marginBottom: 6, fontWeight: 500 }}>
              {t('workflow.builder.row.deleteConfirmFields', { count: placedFields.length })}
            </div>
            <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13 }}>
              {placedFields.map((field) => {
                // Unlabelled fields fall back to their type name, which is
                // already what the canvas chip shows — don't print it twice.
                const typeLabel = t(fieldDisplayLabelKey(field));
                return (
                  <li key={field.id} style={{ marginBottom: 2 }}>
                    <Text strong>{field.labelEn || typeLabel}</Text>
                    {field.labelEn && (
                      <Text type="secondary" style={{ marginLeft: 6, fontSize: 12 }}>
                        {typeLabel}
                      </Text>
                    )}
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </BmoModal>

      {/* `data-cols` as well as the custom property: the tablet band caps the
          count at two, but has to leave a single-column row spanning the full
          width rather than stranding it in a half-width track. */}
      <div
        className="fb-row-body"
        data-cols={columnCount}
        style={{ ...ROW_BODY, '--fb-row-cols': columnCount } as CSSProperties}
      >
        {row.columns.map((col) => (
          <ColumnSlot
            key={col.id}
            col={col}
            sectionId={sectionId}
            rowId={row.id}
            isPalettePending={isPalettePending}
            movingFrom={movingFrom}
            selectedColumnId={selectedColumnId}
            dragging={dragging}
            onPlaceField={onPlaceField}
            onSelectField={onSelectField}
            onArmMove={onArmMove}
            onPlaceMove={onPlaceMove}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * One column of a row: the place a field lives, and the target it is dropped
 * into.
 *
 * Extracted from RowCard's `.map` because it needs `useDroppable` /
 * `useDraggable`, and hooks cannot be called inside a loop body.
 *
 * Carries BOTH interaction models on purpose (see builderDnd.ts): click to
 * arm-and-place, which is the keyboard-reachable path required by WCAG 2.1.1,
 * and drag-and-drop as the pointer enhancement on top.
 */
function ColumnSlot({
  col,
  sectionId,
  rowId,
  isPalettePending,
  movingFrom,
  selectedColumnId,
  dragging,
  onPlaceField,
  onSelectField,
  onArmMove,
  onPlaceMove,
}: {
  col: FormColumn;
  sectionId: string;
  rowId: string;
  isPalettePending: boolean;
  movingFrom: { sectionId: string; rowId: string; columnId: string } | null;
  selectedColumnId: string | null;
  dragging?: BuilderDragData | null;
  onPlaceField: (columnId: string) => void;
  onSelectField: (columnId: string) => void;
  onArmMove: (columnId: string) => void;
  onPlaceMove: (columnId: string) => void;
}) {
  const { t } = useTranslation();
  // Palette-arm and move-arm are mutually exclusive at the page level, so we
  // don't have to worry about both being true at once.
  const armedForPalette = isPalettePending && col.field === null;
  const isMoveSource =
    !!movingFrom &&
    movingFrom.sectionId === sectionId &&
    movingFrom.rowId === rowId &&
    movingFrom.columnId === col.id;
  const armedForMoveDrop = !!movingFrom && !isMoveSource && col.field === null;
  const isSelected = selectedColumnId === col.id;

  const occupied = col.field !== null;
  // Borrowed by an option's inline reveal — the field renders under that
  // option at runtime, not here. Greyed and undraggable so the canvas doesn't
  // imply it appears in this position.
  const borrowedFields = useContext(BorrowedFieldsContext);
  const borrowedBy = col.field ? (borrowedFields.get(col.field.id) ?? null) : null;
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `slot:${col.id}`,
    data: { sectionId, rowId, columnId: col.id, occupied } satisfies BuilderDropData,
    // An occupied slot is not a legal target — the move endpoint fills an EMPTY
    // column, and accepting the drop would silently destroy an authored field.
    disabled: occupied,
  });

  const {
    attributes: dragAttributes,
    listeners: dragListeners,
    setNodeRef: setDragRef,
    isDragging,
  } = useDraggable({
    id: `field:${col.id}`,
    data: {
      kind: 'placedField',
      sectionId,
      rowId,
      columnId: col.id,
    } satisfies BuilderDragData,
    // A borrowed field's position is meaningless (the reveal slot decides
    // where it draws), so moving it is a no-op the author would misread as
    // having done something.
    disabled: !occupied || borrowedBy !== null,
  });

  // Highlight only for a drop that would actually land. `isOver` is true even
  // over a disabled droppable in some dnd-kit paths, and promising a drop that
  // then no-ops is worse than showing nothing.
  const isDropTarget = isOver && !occupied && !!dragging;

  return (
    // Click to select a placed field, to drop an armed field, or to place from
    // the palette. Always focusable — an empty slot is still a valid target,
    // and skipping it would make tab order depend on content (WCAG 2.1.1).
    <div
      ref={setDropRef}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        (e.currentTarget as HTMLDivElement).click();
      }}
      style={{
        ...COLUMN_SLOT,
        ...(armedForPalette
          ? { borderColor: '#0179C1', background: '#EFF6FF', cursor: 'pointer' }
          : {}),
        ...(armedForMoveDrop
          ? {
              borderColor: '#0179C1',
              borderStyle: 'dashed',
              background: '#EFF6FF',
              cursor: 'pointer',
            }
          : {}),
        ...(isMoveSource
          ? {
              borderColor: '#f59e0b',
              borderStyle: 'dashed',
              background: '#FFFBEB',
            }
          : {}),
        ...(isSelected ? { borderColor: '#0179C1', boxShadow: '0 0 0 1px #0179C1' } : {}),
        ...(col.field ? { cursor: 'pointer' } : {}),
        // Borrowed: muted so it reads as "not live in this position". Still
        // selectable — the author edits its label/validation from here and
        // undoes the borrow from the owning option instead.
        ...(borrowedBy !== null
          ? { background: '#f8fafc', borderStyle: 'dashed', borderColor: '#cbd5e1' }
          : {}),
        // Drop feedback wins over the armed styling above — it is the more
        // immediate signal about where the field will actually land.
        ...(isDropTarget
          ? {
              borderColor: '#0179C1',
              borderStyle: 'solid',
              background: '#DBEAFE',
              boxShadow: '0 0 0 2px #0179C1',
            }
          : {}),
        ...(isDragging ? { opacity: 0.4 } : {}),
      }}
      onClick={() => {
        // Move-arm takes precedence when active: clicking an empty column
        // drops; clicking the source again cancels via the page-level handler
        // (handleArmMove toggles).
        if (movingFrom) {
          if (col.field === null) void onPlaceMove(col.id);
          return;
        }
        if (col.field) {
          onSelectField(col.id);
          return;
        }
        if (isPalettePending) {
          onPlaceField(col.id);
        }
      }}
    >
      {col.field ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            width: '100%',
            minWidth: 0,
          }}
        >
          <div style={{ flex: 1, minWidth: 0, opacity: borrowedBy !== null ? 0.55 : 1 }}>
            <FieldPreview field={col.field} />
          </div>
          {borrowedBy !== null && (
            <Tooltip title={t('workflow.builder.column.inlineRevealHint', { field: borrowedBy })}>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  fontSize: 10,
                  lineHeight: 1,
                  fontWeight: 600,
                  color: '#475569',
                  background: '#e2e8f0',
                  padding: '4px 8px',
                  borderRadius: 999,
                  flexShrink: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {t('workflow.builder.column.inlineRevealTag', { field: borrowedBy })}
              </span>
            </Tooltip>
          )}
          {/* No move affordance on a borrowed field — dnd is already disabled
              for it above, and an arm-and-place button that quietly does
              nothing is worse than no button. */}
          {borrowedBy === null && (
            <Tooltip
              // Suppressed mid-drag. A tooltip appearing over a drop target while
              // dragging is exactly what got drag removed from this canvas in
              // June (f8eb21b) — designed out rather than rediscovered.
              open={dragging ? false : undefined}
              title={t(
                isMoveSource
                  ? 'workflow.builder.column.moveCancel'
                  : 'workflow.builder.column.moveStart',
              )}
            >
              <Button
                ref={setDragRef}
                size="small"
                type="text"
                icon={<DragOutlined style={{ fontSize: 13 }} />}
                {...dragAttributes}
                {...dragListeners}
                onClick={(e) => {
                  // Stop propagation so the column's onClick (which would
                  // otherwise treat the click as "select field") doesn't also
                  // fire. A press below the drag-activation distance still
                  // arrives here as a click, which is what keeps the
                  // keyboard/click move path alive.
                  e.stopPropagation();
                  onArmMove(col.id);
                }}
                aria-label={t(
                  isMoveSource
                    ? 'workflow.builder.column.moveCancel'
                    : 'workflow.builder.column.moveStart',
                )}
                style={{
                  flexShrink: 0,
                  cursor: 'grab',
                  color: isMoveSource ? '#ad6800' : '#64748b',
                }}
              />
            </Tooltip>
          )}
        </div>
      ) : (
        <Text type="secondary" style={{ fontSize: 13 }}>
          {isDropTarget
            ? t('workflow.builder.column.dropHere')
            : armedForMoveDrop
              ? t('workflow.builder.column.moveDropHint')
              : isPalettePending
                ? t('workflow.builder.column.dropHint')
                : t('workflow.builder.column.emptyHint')}
        </Text>
      )}
    </div>
  );
}

function FieldPreview({ field }: { field: WorkflowField }) {
  const meta = metaForFieldType(field.type);
  // Display label is type-driven for most fields but switches to a
  // restriction-aware variant (Digits Only / Letters Only / Letters & Numbers)
  // when ShortText/LongText has an InputRestriction set. Keeps the chip
  // honest about what the runtime input will actually accept.
  const displayLabelKey = fieldDisplayLabelKey(field);
  const { t } = useTranslation();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, width: '100%' }}>
      <Text
        strong
        style={{
          fontSize: 13,
          lineHeight: 1.2,
          flex: '0 1 auto',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {field.labelEn || t(displayLabelKey)}
      </Text>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          fontSize: 11,
          lineHeight: 1,
          color: meta.tintFg,
          background: meta.tintBg,
          padding: '3px 8px',
          borderRadius: 999,
          flexShrink: 0,
        }}
      >
        <span style={{ display: 'inline-flex' }}>{meta.icon()}</span>
        <span>{t(displayLabelKey)}</span>
      </span>
    </div>
  );
}
